import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, Phone, Mail, Clock, Award, Linkedin, Facebook, Instagram } from "lucide-react";
import { whatsappLink, brandPhoneLink } from "../lib/whatsapp";

const PUCT_REP = "PUCT REP# Pending";
const PHONE = "(800) 715-6516";
const EMAIL = "support@velcorenergy.com";
const ADDRESS = "440 Louisiana St, Ste 1200, Houston, TX 77002-1063";
const HOURS = "Lun–Vie 9:00 AM – 6:30 PM · Sáb 9:00 AM – 2:00 PM CT";

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer
      className="relative text-velcor-mint pb-16 md:pb-8"
      style={{
        background:
          "radial-gradient(circle at 80% 20%, rgba(47,155,67,0.12), transparent 60%), #0b1324",
      }}
      data-testid="footer"
    >
      <Helmet>
        {/* LocalBusiness schema for SEO */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Velcor Energy",
          "image": "https://velcorenergy.com/velcor-logo.png",
          "telephone": PHONE,
          "email": EMAIL,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "440 Louisiana St, Ste 1200",
            "addressLocality": "Houston",
            "addressRegion": "TX",
            "postalCode": "77002-1063",
            "addressCountry": "US"
          },
          "areaServed": "TX",
          "openingHours": ["Mo-Fr 09:00-18:30", "Sa 09:00-14:00"],
        })}</script>
      </Helmet>

      {/* Top wave — refined "Opción B": shorter, 2 layers, lower opacity,
          smoother curves. Less visual noise so the CTA above keeps the focus. */}
      <div
        aria-hidden
        className="absolute left-0 right-0 w-full pointer-events-none"
        style={{ lineHeight: 0, top: 0, transform: "translateY(-100%)" }}
      >
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="block w-full h-[52px] md:h-[80px]">
          {/* Solid-navy wave silhouette — sharp edge cream→navy, no blur. */}
          <path
            d="M0,58 C240,38 480,84 720,62 C960,40 1200,84 1440,54 L1440,100 L0,100 Z"
            fill="#0b1324"
          />
          {/* Mint horizon line — bottom-most, along the wave crest */}
          <path
            d="M0,58 C240,38 480,84 720,62 C960,40 1200,84 1440,54"
            fill="none"
            stroke="#7ed957"
            strokeWidth="1.5"
            strokeOpacity="0.85"
            strokeLinecap="round"
          />
          {/* Bolt-yellow accent — middle line. Color from the lightning bolt (#d8e465). */}
          <path
            d="M0,49 C240,29 480,75 720,53 C960,31 1200,75 1440,45"
            fill="none"
            stroke="#d8e465"
            strokeWidth="0.9"
            strokeOpacity="0.92"
            strokeLinecap="round"
          />
          {/* V-blue accent — topmost line. Color from the "V" in the logo (#0e6acd). */}
          <path
            d="M0,40 C240,20 480,66 720,44 C960,22 1200,66 1440,36"
            fill="none"
            stroke="#0e6acd"
            strokeWidth="0.85"
            strokeOpacity="0.88"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="relative max-w-[1180px] mx-auto px-6 pt-10 md:pt-12 grid gap-8 md:gap-10 md:grid-cols-12">
        {/* Brand + address + trust */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-3">
            <img src="/velcor-logo.png" alt="Velcor Energy" width={44} height={44} className="object-contain shrink-0" style={{ width: 44, height: 44 }} />
            <span className="font-black text-[16px] tracking-tight">VELCOR ENERGY</span>
          </div>
          <p className="text-velcor-mint/75 max-w-[380px] mb-5 text-[13.5px] leading-relaxed">
            {t("footer.tagline")}
          </p>

          {/* Trust row */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-velcor-green/15 border border-velcor-green/40 px-3 py-1 text-[11px] font-extrabold text-velcor-mint">
              <ShieldCheck className="w-3 h-3" /> {PUCT_REP}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 px-3 py-1 text-[11px] font-extrabold text-amber-200">
              <Award className="w-3 h-3" /> BBB Pendiente
            </span>
          </div>

          {/* Address */}
          <div className="space-y-2 text-[13px] text-velcor-mint/85">
            <div className="flex items-start gap-2.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-velcor-green shrink-0" />
              <span>{ADDRESS}</span>
            </div>
            <div className="flex items-start gap-2.5">
              <Clock className="w-3.5 h-3.5 mt-0.5 text-velcor-green shrink-0" />
              <span>{HOURS}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Phone className="w-3.5 h-3.5 text-velcor-green shrink-0" />
              <a href={brandPhoneLink()} className="hover:text-white transition-colors">{PHONE}</a>
            </div>
            <div className="flex items-center gap-2.5">
              <Mail className="w-3.5 h-3.5 text-velcor-green shrink-0" />
              <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors">{EMAIL}</a>
            </div>
          </div>
        </div>

        {/* Nav columns */}
        <div className="md:col-span-2">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green/85 mb-3">
            {t("footer.colCompany")}
          </div>
          <ul className="space-y-2 text-[13.5px] text-velcor-mint/85">
            <li><Link to="/about" className="hover:text-white transition-colors">{t("nav.about")}</Link></li>
            <li><Link to="/how-it-works" className="hover:text-white transition-colors">{t("nav.how")}</Link></li>
            <li><Link to="/shop" className="hover:text-white transition-colors">{t("nav.plans")}</Link></li>
            <li><Link to="/blog" className="hover:text-white transition-colors" data-testid="footer-blog">Blog</Link></li>
            <li><Link to="/support" className="hover:text-white transition-colors">{t("nav.support")}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green/85 mb-3">
            {t("footer.colCities", "Ciudades")}
          </div>
          <ul className="space-y-2 text-[13.5px] text-velcor-mint/85">
            <li><Link to="/electricidad-houston" className="hover:text-white transition-colors" data-testid="footer-city-houston">Houston</Link></li>
            <li><Link to="/electricidad-dallas" className="hover:text-white transition-colors" data-testid="footer-city-dallas">Dallas</Link></li>
            <li><Link to="/electricidad-san-antonio" className="hover:text-white transition-colors" data-testid="footer-city-san-antonio">San Antonio</Link></li>
            <li><Link to="/electricidad-fort-worth" className="hover:text-white transition-colors" data-testid="footer-city-fort-worth">Fort Worth</Link></li>
            <li><Link to="/electricidad-arlington" className="hover:text-white transition-colors" data-testid="footer-city-arlington">Arlington</Link></li>
            <li><Link to="/electricidad-corpus-christi" className="hover:text-white transition-colors" data-testid="footer-city-corpus-christi">Corpus Christi</Link></li>
            <li><Link to="/electricidad-galveston" className="hover:text-white transition-colors" data-testid="footer-city-galveston">Galveston</Link></li>
            <li><Link to="/electricidad-waco" className="hover:text-white transition-colors" data-testid="footer-city-waco">Waco</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green/85 mb-3">
            {t("footer.colCustomer")}
          </div>
          <ul className="space-y-2 text-[13.5px] text-velcor-mint/85">
            <li><a href={whatsappLink()} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">WhatsApp</a></li>
            <li><a href={brandPhoneLink()} className="hover:text-white transition-colors">{PHONE}</a></li>
            <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
            <li><Link to="/portal/login" className="hover:text-white transition-colors" data-testid="footer-portal-login">{t("nav.signin")}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green/85 mb-3">
            {t("footer.colLegal")}
          </div>
          <ul className="space-y-2 text-[13.5px] text-velcor-mint/85">
            <li><Link to="/privacy" className="hover:text-white transition-colors">{t("footer.privacy")}</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors">{t("footer.terms")}</Link></li>
            <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link></li>
            <li><Link to="/your-rights" className="hover:text-white transition-colors">YRAC</Link></li>
            <li><Link to="/electricity-facts-label/flexible" className="hover:text-white transition-colors">EFL</Link></li>
          </ul>
        </div>
      </div>

      {/* Social + bottom legal bar */}
      <div className="max-w-[1180px] mx-auto px-6 mt-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
        <div className="flex items-center gap-2">
          <a href="https://www.facebook.com/share/1B5itAuGZS/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="grid place-items-center w-8 h-8 rounded-full bg-white/5 hover:bg-velcor-green/20 transition-colors text-velcor-mint/80 hover:text-white" data-testid="footer-facebook">
            <Facebook className="w-3.5 h-3.5" />
          </a>
          <a href="https://www.instagram.com/velcorenergy?igsh=MTZ3cHBmbnJvZGxicg%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="grid place-items-center w-8 h-8 rounded-full bg-white/5 hover:bg-velcor-green/20 transition-colors text-velcor-mint/80 hover:text-white" data-testid="footer-instagram">
            <Instagram className="w-3.5 h-3.5" />
          </a>
          <a href="#" aria-label="LinkedIn" className="grid place-items-center w-8 h-8 rounded-full bg-white/5 hover:bg-velcor-green/20 transition-colors text-velcor-mint/80 hover:text-white">
            <Linkedin className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      <div className="max-w-[1180px] mx-auto px-6 border-t border-white/10 mt-5 pt-4 pb-1 md:pr-32 flex flex-col md:flex-row md:justify-between gap-3 text-[11.5px] text-velcor-mint/55 leading-relaxed">
        <span>© {new Date().getFullYear()} Velcor Energy LLC. Todos los derechos reservados. {PUCT_REP}.</span>
        <span>Houston, TX · USA</span>
      </div>
    </footer>
  );
}
